import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Phone, Mail, Star, Wrench, Clock, ShieldCheck, Heart, Quote } from "lucide-react";
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string; desc: string; product: string; city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const services = [
  { title: "Rénovation complète", desc: "De la conception à la finition, nous transformons vos espaces avec un savoir-faire artisanal.", price: "Sur devis" },
  { title: "Installation sur mesure", desc: "Menuiserie, plomberie, électricité — chaque installation est réalisée avec précision.", price: "Dès 150 EUR" },
  { title: "Dépannage urgent", desc: "Intervention rapide sous 2h pour tous vos besoins urgents.", price: "Dès 80 EUR" },
  { title: "Entretien régulier", desc: "Contrats de maintenance préventive pour la sérénité de votre habitat.", price: "Forfait mensuel" },
];

const ArtisanTemplate = ({ name, desc, product, city, customizations, editMode = false, onCustomize }: Props) => {
  const c = customizations || {};
  const edit = (field: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(field, v);
  const displayName = c.heroTitle || name;
  const displayDesc = c.heroSubtitle || desc;
  const displayProduct = c.productName || product || "Artisan qualifié";
  const displayPhone = c.phone || "01 23 45 67 89";
  return (
  <div className="bg-white text-[#292524] min-h-screen">
    {/* Nav */}
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-amber-600" />
          <EditableText as="span" value={displayName} onChange={edit("heroTitle")} editMode={editMode} className="text-lg font-bold text-amber-700" data-field="brand-name" />
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
          <span className="hover:text-amber-600 cursor-pointer">Services</span>
          <span className="hover:text-amber-600 cursor-pointer">Réalisations</span>
          <span className="hover:text-amber-600 cursor-pointer">Témoignages</span>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white h-9 px-5 rounded-lg text-sm">
            <Phone className="h-3.5 w-3.5 mr-1.5" /> Appeler
          </Button>
        </div>
      </div>
    </nav>

    {/* Hero */}
    <section className="relative bg-gradient-to-br from-amber-50 via-white to-orange-50 py-24">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <div className="flex">
              {[1,2,3,4,5].map(i => <Star key={i} className="h-4 w-4 text-amber-500 fill-amber-500" />)}
            </div>
            <span className="text-sm text-gray-500">4.9/5 — 180+ avis</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight text-[#292524]">
            <EditableText as="span" value={displayProduct} onChange={edit("productName")} editMode={editMode} data-field="hero-title" /><br />
            <span className="text-amber-600">à {city}</span>
          </h1>
          <EditableText as="p" value={displayDesc} onChange={edit("heroSubtitle")} editMode={editMode} multiline className="text-lg text-gray-600 max-w-md" data-field="hero-subtitle" />
          <div className="flex gap-4 pt-2">
            <Button className="bg-amber-600 hover:bg-amber-700 text-white h-14 px-8 rounded-xl text-base">
              Devis gratuit <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" className="h-14 px-8 rounded-xl border-gray-300 text-gray-700">
              <Phone className="mr-2 h-4 w-4" /> <EditableText as="span" value={displayPhone} onChange={edit("phone")} editMode={editMode} data-field="phone" />
            </Button>
          </div>
          <div className="flex gap-6 pt-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-amber-600" />Garantie décennale</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-600" />Intervention sous 48h</span>
          </div>
        </div>
        <div className="hidden lg:block">
          <div className="aspect-[4/3] rounded-3xl bg-gradient-to-br from-amber-200/50 to-orange-100/50 border border-amber-200/50 flex items-center justify-center">
            <div className="text-center space-y-3">
              <Wrench className="h-16 w-16 text-amber-600/30 mx-auto" />
              <p className="text-amber-700/50 text-sm font-medium">Photo de vos réalisations</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Services */}
    <section className="py-24 bg-[#fafaf9]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-amber-600">Nos services</span>
          <h2 className="text-4xl font-bold mt-2">Ce que nous faisons pour vous</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((s) => (
            <div key={s.title} className="bg-white rounded-2xl border border-gray-100 p-8 hover:border-amber-200 hover:shadow-lg hover:shadow-amber-50 transition-all">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold">{s.title}</h3>
                <span className="text-amber-600 font-bold text-sm shrink-0 ml-4">{s.price}</span>
              </div>
              <p className="text-gray-600 leading-relaxed">{s.desc}</p>
              <Button variant="ghost" className="text-amber-600 hover:text-amber-700 mt-4 px-0">
                En savoir plus <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Testimonials */}
    <section className="py-24 bg-amber-600 text-white">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Quote className="h-10 w-10 mx-auto mb-6 opacity-50" />
        <blockquote className="text-2xl md:text-3xl font-medium leading-relaxed italic">
          "Un artisan remarquable. Travail soigné, respect des délais et un résultat qui dépasse nos attentes. Je recommande les yeux fermés."
        </blockquote>
        <div className="mt-8">
          <p className="font-semibold">Marie et Pierre L.</p>
          <p className="text-amber-200 text-sm">Rénovation cuisine — {city}</p>
        </div>
        <div className="flex justify-center gap-1 mt-4">
          {[1,2,3,4,5].map(i => <Star key={i} className="h-5 w-5 text-amber-200 fill-amber-200" />)}
        </div>
      </div>
    </section>

    {/* Contact */}
    <section className="py-24">
      <div className="max-w-6xl mx-auto px-6 text-center space-y-6">
        <h2 className="text-4xl font-bold">Un projet ? Parlons-en.</h2>
        <p className="text-lg text-gray-600">Devis gratuit et sans engagement sous 24h.</p>
        <div className="flex justify-center gap-8 text-gray-600">
          <span className="flex items-center gap-2"><Phone className="h-5 w-5 text-amber-600" />01 23 45 67 89</span>
          <span className="flex items-center gap-2"><Mail className="h-5 w-5 text-amber-600" />contact@{name.toLowerCase().replace(/\s/g,"")}.fr</span>
          <span className="flex items-center gap-2"><MapPin className="h-5 w-5 text-amber-600" />{city}</span>
        </div>
        <Button className="bg-amber-600 hover:bg-amber-700 text-white h-14 px-10 rounded-xl text-base">
          Demander un devis gratuit <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </section>

    {/* Footer */}
    <footer className="bg-[#292524] text-white py-12">
      <div className="max-w-6xl mx-auto px-6 text-center text-sm text-gray-400">
        <p className="text-amber-500 font-bold text-lg mb-2">{name}</p>
        © 2026 — {city} — Propulsé par Grafton IA
      </div>
    </footer>
  </div>
  );
};

export default ArtisanTemplate;
